/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Gnoment, gnoment } from 'shared-ui/gnoment';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Label, Counter, Icon, FlexRow, FlexCol, Button } from 'sarsaparilla';

import DateRangePickerCalendar from '../../range-date-picker-calendar/DateRangePickerCalendar';
import SingleDatePickerCalendar from '../../../containers/single-date-picker-calendar';
import SmartSelector from '../../registration/SmartSelector';

const propTypes = {
    index: PropTypes.number.isRequired,
    handleChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func,
    validationErrors: PropTypes.shape({
        divisionRequired: PropTypes.string,
        groupMaxRequired: PropTypes.string,
        dateRangeRequired: PropTypes.string,
    }),

    // Counter
    maxGroupSize: PropTypes.number,
    minGroupSize: PropTypes.number,

    // User Selections
    selectedDateRangeStart: PropTypes.instanceOf(Gnoment),
    selectedDateRangeEnd: PropTypes.instanceOf(Gnoment),
    selectedDivisionId: PropTypes.string,
    selectedGroupSize: PropTypes.number,

    // Datepickers
    currentDate: PropTypes.instanceOf(Gnoment),
    displayDate: PropTypes.instanceOf(Gnoment),
    blockFilter: PropTypes.func,

    // Division Dropdown
    divisionChoices: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            label: PropTypes.string,
        })
    ),
};

const defaultProps = {
    selectedGroupSize: 0,
};

export const dateTypesEnum = {
    SingleDate: 'SingleDate',
    DateRange: 'DateRange',
};

export default class DateRangeGroupSizeItem extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            dateType: dateTypesEnum.SingleDate,
            savedSingleDate: props.selectedDateRangeStart,
            savedDateRangeStart: props.selectedDateRangeStart,
            savedDateRangeEnd: props.selectedDateRangeEnd,
        };

        this.handleDateTypeRadioChange = this.handleDateTypeRadioChange.bind(this);
        this.handleDateRangeChange = this.handleDateRangeChange.bind(this);
        this.handleSingleDateChange = this.handleSingleDateChange.bind(this);
    }

    componentDidMount() {
        const { selectedDateRangeStart, selectedDateRangeEnd } = this.props;

        if (selectedDateRangeStart && selectedDateRangeEnd) {
            this.setState({
                dateType: gnoment(selectedDateRangeStart).isSame(
                    gnoment(selectedDateRangeEnd)
                )
                    ? dateTypesEnum.SingleDate
                    : dateTypesEnum.DateRange,
            });
        }
    }

    handleDateTypeRadioChange(e) {
        const { value } = e.target;
        this.setState({ dateType: value });

        const { handleChange } = this.props;
        const { savedSingleDate, savedDateRangeStart, savedDateRangeEnd } = this.state;

        // when swapping date types, remember its last chosen value
        if (value === dateTypesEnum.SingleDate) {
            handleChange({
                preferred_range_start: savedSingleDate,
                preferred_range_end: savedSingleDate,
            });
        } else if (value === dateTypesEnum.DateRange) {
            handleChange({
                preferred_range_start: savedDateRangeStart,
                preferred_range_end: savedDateRangeEnd,
            });
        }
    }

    handleDateRangeChange({ startDate, endDate }) {
        this.setState({ savedDateRangeStart: startDate, savedDateRangeEnd: endDate });

        const { handleChange } = this.props;
        handleChange({ preferred_range_start: startDate, preferred_range_end: endDate });
    }

    handleSingleDateChange(day) {
        this.setState({ savedSingleDate: day });

        const { handleChange } = this.props;
        handleChange({ preferred_range_start: day, preferred_range_end: day });
    }

    handleDivisionTypeChange(divisionId) {
        const { selectedDivisionId, minGroupSize, handleChange } = this.props;

        if (divisionId !== selectedDivisionId) {
            const resetValues = {
                preferred_range_start: null,
                preferred_range_end: null,
                group_min_size: minGroupSize,
                group_max_size: minGroupSize,
            };
            handleChange({ ...resetValues, division_id: divisionId });
        }
    }

    renderDivisionTypeDropdown() {
        const { index, divisionChoices, selectedDivisionId, validationErrors } =
            this.props;

        return (
            !isEmpty(divisionChoices) && (
                <FlexCol md={3}>
                    <div
                        className={`rec-form-item-wrap ${get(validationErrors, 'divisionRequired', '')}`}
                    >
                        <SmartSelector
                            name={`dropdown-division-${index}`}
                            id={`dropdown-division-${index}`}
                            label="Permit Type"
                            value={selectedDivisionId}
                            handleChange={(e) =>
                                this.handleDivisionTypeChange(e.target.value)
                            }
                            options={divisionChoices}
                            isRequired
                        />
                    </div>
                </FlexCol>
            )
        );
    }

    renderDateTypeRadioButtons() {
        const { index } = this.props;
        const { dateType } = this.state;

        return (
            <fieldset>
                <div className="rec-form-inline-item-wrap">
                    <div className="rec-form-inline-item">
                        <label
                            htmlFor={`radio-single-date-${index}`}
                            className="rec-label-radio"
                        >
                            <input
                                className="rec-input-hide"
                                type="radio"
                                id={`radio-single-date-${index}`}
                                name={`radio-single-date-${index}`}
                                placeholder="Single Date"
                                value={dateTypesEnum.SingleDate}
                                checked={dateType === dateTypesEnum.SingleDate}
                                onChange={this.handleDateTypeRadioChange}
                            />
                            <span className="rec-input-radio" />
                            Single Date
                        </label>
                    </div>
                    <div className="rec-form-inline-item">
                        <label
                            htmlFor={`radio-date-range-${index}`}
                            className="rec-label-radio"
                        >
                            <input
                                className="rec-input-hide"
                                type="radio"
                                id={`radio-date-range-${index}`}
                                name={`radio-date-range-${index}`}
                                placeholder="Date Range"
                                value={dateTypesEnum.DateRange}
                                checked={dateType === dateTypesEnum.DateRange}
                                onChange={this.handleDateTypeRadioChange}
                            />
                            <span className="rec-input-radio" />
                            Date Range
                        </label>
                    </div>
                </div>
            </fieldset>
        );
    }

    renderDatePicker() {
        const {
            index,
            currentDate,
            blockFilter,
            displayDate,
            selectedDateRangeStart,
            selectedDateRangeEnd,
            selectedDivisionId,
            divisionChoices,
            validationErrors,
        } = this.props;
        const { dateType } = this.state;

        // give calendar more space if there's no division choice dropdown
        const calendarClassName = isEmpty(divisionChoices)
            ? 'item-xx-large'
            : 'item-x-large';

        return dateType === dateTypesEnum.DateRange ? (
            <FlexCol md={6}>
                <div className={`rec-form-item-wrap ${calendarClassName}`}>
                    <Label htmlFor={`range-date-picker-${index}`} isRequired>
                        First Date Available Within
                    </Label>
                    <div
                        className={`permit-availability-calendar ${get(validationErrors, 'dateRangeRequired', '')}`}
                    >
                        <DateRangePickerCalendar
                            showDefaultInputIcon
                            currentDate={currentDate}
                            id={`range-date-picker-${index}`}
                            blockFilter={blockFilter}
                            divisionId={selectedDivisionId}
                            dateRangeStart={selectedDateRangeStart}
                            dateRangeEnd={selectedDateRangeEnd}
                            onDatesChange={this.handleDateRangeChange}
                            initialVisibleMonth={displayDate}
                            blockNonLotteryDates
                            isRequired
                        />
                    </div>
                </div>
            </FlexCol>
        ) : (
            <FlexCol md={6}>
                <div className={`rec-form-item-wrap ${calendarClassName}`}>
                    <Label htmlFor={`entry-date-picker-${index}`} isRequired>
                        Entry Date
                    </Label>
                    <div className={get(validationErrors, 'dateRangeRequired', '')}>
                        <SingleDatePickerCalendar
                            showDefaultInputIcon
                            currentDate={currentDate}
                            id={`entry-date-picker-${index}`}
                            blockFilter={blockFilter}
                            divisionId={selectedDivisionId}
                            selectedDate={selectedDateRangeStart}
                            onChange={this.handleSingleDateChange}
                            initialVisibleMonth={displayDate}
                            renderLabels={false}
                            blockNonLotteryDates
                        />
                    </div>
                </div>
            </FlexCol>
        );
    }

    renderCounter() {
        const { index, selectedGroupSize, handleChange, maxGroupSize, validationErrors } =
            this.props;

        return (
            <FlexCol md={3}>
                <div className="rec-form-item-wrap">
                    <Label htmlFor={`groupSize-${index}`} isRequired>
                        Group Size
                    </Label>
                    <Counter
                        id={`groupSize-${index}`}
                        count={selectedGroupSize}
                        max={maxGroupSize}
                        validationClass={get(validationErrors, 'groupMaxRequired', '')}
                        onCountChange={(ct) =>
                            handleChange({ group_min_size: ct, group_max_size: ct })
                        }
                    />
                </div>
            </FlexCol>
        );
    }

    renderSelectionItems() {
        const { index, onRemove } = this.props;

        return (
            <div className="per-form-group-item">
                <div className="per-form-group-item-header">
                    <div className="per-form-group-item-title">
                        Choice <span id={`choiceNumber-${index}`}>#{index + 1}</span>
                    </div>
                    <Button
                        appearance="subtle-danger"
                        id="removeIcon"
                        iconBeforeElement={<Icon iconName="delete" />}
                        onClick={() => onRemove(index)}
                        screenReaderTextBefore={`Remove choice ${this.props.index + 1} from the lottery preference list`}
                    />
                </div>
                <FlexRow alignItems="end">
                    {this.renderDivisionTypeDropdown()}
                    {this.renderDatePicker()}
                    {this.renderCounter()}
                </FlexRow>
            </div>
        );
    }

    render() {
        return (
            <div>
                {this.renderDateTypeRadioButtons()}
                {this.renderSelectionItems()}
            </div>
        );
    }
}

DateRangeGroupSizeItem.propTypes = propTypes;
DateRangeGroupSizeItem.defaultProps = defaultProps;
